/**
 * Firebase configuration and initialization
 */
import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import { getAuth, Auth, initializeRecaptchaConfig } from 'firebase/auth';
import { getFirestore, Firestore } from 'firebase/firestore';
import { getStorage, FirebaseStorage } from 'firebase/storage';
import { getAnalytics, Analytics, isSupported } from 'firebase/analytics';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

// Initialize Firebase (prevent multiple initializations)
let app: FirebaseApp;
let auth: Auth;
let db: Firestore;
let storage: FirebaseStorage;
let analytics: Analytics | null = null;

if (!getApps().length) {
  app = initializeApp(firebaseConfig);
} else {
  app = getApps()[0];
}

auth = getAuth(app);

// Firebase blocks real phone auth from localhost since July 2024.
// Dev: use appVerificationDisabledForTesting + test phone numbers from Firebase Console.
// Prod: initializeRecaptchaConfig enables reCAPTCHA Enterprise for real phone auth.
if (process.env.NODE_ENV !== 'production') {
  auth.settings.appVerificationDisabledForTesting = true;
}

initializeRecaptchaConfig(auth).catch(() => {});

db = getFirestore(app);
storage = getStorage(app);

// Initialize Analytics only in browser and if supported
if (typeof window !== 'undefined') {
  isSupported().then((supported) => {
    if (supported) {
      analytics = getAnalytics(app);
    }
  });
}

export { app, auth, db, storage, analytics };

// Collection names as constants
export const COLLECTIONS = {
  USERS: 'users',
  PRODUCTS: 'products',
  ORDERS: 'orders',
  CATEGORIES: 'categories',
  BRANDS: 'brands',
  COUPONS: 'coupons',
  REVIEWS: 'reviews',
  WISHLISTS: 'wishlists',
  NOTIFICATIONS: 'notifications',
  SETTINGS: 'settings',
  ORDER_LOOKUPS: 'orderLookups',
  SAVED_FOR_LATER: 'savedForLater',
  CONTACT_INQUIRIES: 'contactInquiries',
} as const;
